import { ReactNode } from 'react';
import { UserAccessModalTranslations } from './getUserAccessModalTranslations';
import { css } from '@bts-web/utils-style-engine';
import { FilledWarning, RegularEye, RegularInfo } from '../../svgIcons';

export type ModalDataConfig = {
  icon: ReactNode;
  title: string;
  message: string;
  action: {
    component?: ReactNode;
    label?: string;
    to?: string;
  };
};

export enum AccessInfoType {
  ReadOnly = 'readOnly',
  InfoApprovalPending = 'infoApprovalPending',
  WarningTrade = 'warningTrade',
  WarningTransfer = 'warningTransfer',
  CoolOffActive = 'coolOffActive',
  CoolOffNeedsAA = 'coolOffNeedsAA',
  WaitingPeriodActive = 'waitingPeriodActive',
  FinalCheckPending = 'finalCheckPending',
  UkTradeCoolOffActive = 'ukTradeCoolOffActive',
  UkTradeCoolOffWaiting = 'ukTradeCoolOffWaiting',
}

const modalIconStyles = css({
  mb: 'extra_small_2',
  '&[data-element="icon-warning"]': {
    color: 'warning.fill_primary',
  },
  '&[data-element="icon-info"]': {
    color: 'positive.fill_primary',
  },
});

export const getAccessModalInfo = (
  translations: UserAccessModalTranslations,
  type: AccessInfoType,
): ModalDataConfig => {
  const config: { [key: string]: ModalDataConfig } = {
    readOnly: {
      icon: (
        <RegularEye
          size={48}
          data-element="icon-info"
          className={modalIconStyles}
        />
      ),
      title: translations.userRoleModalReadOnlyTitle,
      message: translations.userRoleModalReadOnlySubtitle,
      action: {
        label: translations.goToPortfolio,
        to: '/portfolio',
      },
    },
    infoApprovalPending: {
      icon: (
        <FilledWarning
          size={48}
          data-element="icon-warning"
          className={modalIconStyles}
        />
      ),
      title: translations.userStageInfoPendingApproval,
      message: translations.userStageModalPendingApprovalSubtitle,
      action: {
        label: translations.goToPortfolio,
        to: '/portfolio',
      },
    },
    warningTrade: {
      icon: (
        <FilledWarning
          size={48}
          data-element="icon-warning"
          className={modalIconStyles}
        />
      ),
      title: translations.errorTitleTradingUnavailable,
      message: translations.errorMessageTradingUnavailable,
      action: {
        label: translations.goToPortfolio,
        to: '/portfolio',
      },
    },
    warningTransfer: {
      icon: (
        <FilledWarning
          size={48}
          data-element="icon-warning"
          className={modalIconStyles}
        />
      ),
      title: translations.errorTitleTransfersUnavailable,
      message: translations.errorMessagePendingApprovalTransfers,
      action: {
        label: translations.goToPortfolio,
        to: '/portfolio',
      },
    },
    ukTradeCoolOffActive: {
      icon: (
        <RegularInfo
          size={48}
          data-element="icon-info"
          className={modalIconStyles}
        />
      ),
      title: translations.coolOffActiveTitle,
      message: translations.coolOffActiveMessage,
      action: {
        label: translations.gotIt,
      },
    },
    coolOffActive: {
      icon: (
        <RegularInfo
          size={48}
          data-element="icon-info"
          className={modalIconStyles}
        />
      ),
      title: translations.coolOffActiveTitle,
      message: translations.coolOffActiveMessage,
      action: {
        label: translations.gotIt,
      },
    },
    coolOffNeedsAA: {
      icon: (
        <RegularInfo
          size={48}
          data-element="icon-info"
          className={modalIconStyles}
        />
      ),
      title: translations.coolOffNeedsAATitle,
      message: translations.coolOffNeedsAAMessage,
      action: {
        label: translations.goToFinancialQuiz,
        to: '/appropriateness-questions/introduction?next=portfolio',
      },
    },
    waitingPeriodActive: {
      icon: (
        <RegularInfo
          size={48}
          data-element="icon-info"
          className={modalIconStyles}
        />
      ),
      title: translations.waitingPeriodActiveTitle,
      message: translations.waitingPeriodActiveMessage,
      action: {
        label: translations.gotIt,
      },
    },
    finalCheckPending: {
      icon: (
        <RegularInfo
          size={48}
          data-element="icon-info"
          className={modalIconStyles}
        />
      ),
      title: translations.finalCheckPendingTitle,
      message: translations.finalCheckPendingMessage,
      action: {
        label: translations.goToPortfolio,
        to: '/portfolio',
      },
    },
  };

  return config[type] || {};
};
